import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { useBrreg } from '../../context/BrregContext';
import { useCRMData } from '../../context/CRMDataContext';
import { useTeam } from '../../context/TeamContext';
import { getCanonicalOrgNumber } from '../../services/BrregService';

const isUuid = value => (
  typeof value === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
);

const getEntityId = entity => entity?.id || entity?.company_id;

const getOwnerId = owner => owner?.user_id || owner?.id;

const getCompanyOrgNr = company => getCanonicalOrgNumber(company);

const getPipelineId = pipeline => pipeline?.id || pipeline?.pipeline_id;

const isOpenDeal = deal => ![
  'Lukket',
  'Vunnet',
  'Tapt',
  'Avsluttet',
  'Closed',
  'Won',
  'Lost'
].includes(String(deal?.status || '').trim());

const isDuplicateLead = lead => (
  ['duplicate', 'duplikat'].includes(
    String(lead?.status || lead?.wash_status || '').trim().toLowerCase()
  ) ||
  Boolean(lead?.duplicateReason || lead?.duplicate_reason)
);

const ExportWizard = ({ onClose }) => {
  const { stagingLeads, updateLeadStatus, settings } = useBrreg();
  const { users } = useTeam();
  const {
    addCompany,
    addDeal,
    companies = [],
    deals = [],
    projects = [],
    pipelineStages = [],
    clientPipelines = []
  } = useCRMData();

  const [step, setStep] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [exportResult, setExportResult] = useState(null);
  const [pipelineError, setPipelineError] = useState('');
  const [distributionError, setDistributionError] = useState('');
  const [filters, setFilters] = useState({
    minScore: 40,
    hasEmail: false,
    hasPhone: false,
    fylke: '',
    naeringskode: ''
  });
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [targetConfig, setTargetConfig] = useState({
    targetType: 'Global',
    projectId: '',
    createDeal: false,
    pipelineId: '',
    stageId: 'stage-1'
  });
  const [distConfig, setDistConfig] = useState({
    mode: 'Auto',
    selectedOwners: [],
    singleOwnerId: '',
    manualMap: {},
    conflictPolicy: 'Skip'
  });
  const [rulesConfig, setRulesConfig] = useState({
    gdprConfirmed: false,
    deltaUpdate: false
  });

  const validTeamUsers = useMemo(() => (
    (Array.isArray(users) ? users : [])
      .map(user => ({
        ...user,
        ownerId: getOwnerId(user)
      }))
      .filter(user => isUuid(user.ownerId))
  ), [users]);

  const validTeamOwnerIds = useMemo(
    () => new Set(validTeamUsers.map(user => user.ownerId)),
    [validTeamUsers]
  );

  const approvedLeads = useMemo(
    () => stagingLeads.filter(lead => (
      lead.wash_status === 'Godkjent' &&
      !isDuplicateLead(lead)
    )),
    [stagingLeads]
  );

  const filteredLeads = useMemo(() => (
    approvedLeads.filter(lead => {
      if (lead.data_quality_score < filters.minScore) return false;
      if (filters.hasEmail && !lead.email) return false;
      if (filters.hasPhone && !lead.phone) return false;
      if (filters.fylke && lead.fylke !== filters.fylke) return false;
      if (filters.naeringskode && !lead.naeringskode?.startsWith(filters.naeringskode)) {
        return false;
      }
      return true;
    })
  ), [approvedLeads, filters]);

  const validPipelineStages = useMemo(() => (
    pipelineStages
      .map(stage => ({
        ...stage,
        stage_id: stage?.id || stage?.stage_id,
        stage_name: stage?.name || stage?.title || stage?.label
      }))
      .filter(stage => isUuid(stage.stage_id) && stage.stage_id !== 'stage-1')
  ), [pipelineStages]);

  const availablePipelines = useMemo(() => {
    if (targetConfig.targetType !== 'Project' || !targetConfig.projectId) {
      return clientPipelines;
    }

    return clientPipelines.filter(pipeline => (
      pipeline.project_id === targetConfig.projectId
    ));
  }, [
    clientPipelines,
    targetConfig.projectId,
    targetConfig.targetType
  ]);

  useEffect(() => {
    const selectedStageExists = validPipelineStages.some(stage => (
      stage.stage_id === targetConfig.stageId
    ));

    if (
      validPipelineStages.length > 0 &&
      !selectedStageExists &&
      targetConfig.stageId !== validPipelineStages[0].stage_id
    ) {
      setTargetConfig(current => ({
        ...current,
        stageId: validPipelineStages[0].stage_id
      }));
    }

    if (validPipelineStages.length === 0 && targetConfig.stageId) {
      setTargetConfig(current => ({
        ...current,
        stageId: ''
      }));
    }
  }, [targetConfig.stageId, validPipelineStages]);

  useEffect(() => {
    if (!targetConfig.createDeal) return;

    const availableIds = availablePipelines
      .map(getPipelineId)
      .filter(Boolean);

    if (
      availableIds.length === 1 &&
      targetConfig.pipelineId !== availableIds[0]
    ) {
      setTargetConfig(current => ({
        ...current,
        pipelineId: availableIds[0]
      }));
      setPipelineError('');
      return;
    }

    if (
      targetConfig.pipelineId &&
      !availableIds.includes(targetConfig.pipelineId)
    ) {
      setTargetConfig(current => ({
        ...current,
        pipelineId: ''
      }));
    }
  }, [
    availablePipelines,
    targetConfig.createDeal,
    targetConfig.pipelineId
  ]);

  const handleClose = () => {
    if (isExporting) return;
    onClose?.();
  };

  const handleSelectAll = checked => {
    if (checked) setSelectedLeadIds(filteredLeads.map(lead => lead.id));
    else setSelectedLeadIds([]);
  };

  const handleToggleLead = id => {
    setSelectedLeadIds(prev => (
      prev.includes(id)
        ? prev.filter(item => item !== id)
        : [...prev, id]
    ));
  };

  const handleNextStep = () => {
    if (
      step === 3 &&
      distConfig.mode === 'Single' &&
      !isUuid(distConfig.singleOwnerId)
    ) {
      setDistributionError(
        'Velg en selger, eller bytt til eksport uten eier.'
      );
      return;
    }

    setDistributionError('');

    if (step < 5) setStep(current => current + 1);
    else executeExport();
  };

  const findExistingCompany = lead => {
    const leadOrgNumber = getCanonicalOrgNumber(lead);

    if (leadOrgNumber) {
      const orgnrMatch = companies.find(company => (
        getCompanyOrgNr(company) === leadOrgNumber
      ));

      if (orgnrMatch) {
        return {
          company: orgnrMatch,
          reason: 'Finnes allerede i CRM',
          matchType: 'org_number'
        };
      }
    }

    if (!leadOrgNumber && lead.name) {
      const normalizedLeadName = lead.name.trim().toLowerCase();
      const nameMatch = companies.find(company => (
        String(company.name || '').trim().toLowerCase() === normalizedLeadName
      ));

      if (nameMatch) {
        return {
          company: nameMatch,
          reason: 'Finnes allerede i CRM',
          matchType: 'name'
        };
      }
    }

    return null;
  };

  const findExistingDeal = (companyId, pipelineId, title) => (
    deals.find(deal => (
      getEntityId(deal) === companyId &&
      getPipelineId(deal) === pipelineId &&
      String(deal.title || '').trim() === title.trim() &&
      isOpenDeal(deal)
    )) || null
  );

  const executeExport = async () => {
    if (
      distConfig.mode === 'Single' &&
      !isUuid(distConfig.singleOwnerId)
    ) {
      setDistributionError(
        'Velg en selger, eller bytt til eksport uten eier.'
      );
      setStep(3);
      return;
    }

    const selectedPipelineId = targetConfig.pipelineId;
    const selectedPipelineIsAvailable = availablePipelines.some(pipeline => (
      getPipelineId(pipeline) === selectedPipelineId
    ));

    if (
      targetConfig.createDeal &&
      (
        !isUuid(selectedPipelineId) ||
        !selectedPipelineIsAvailable
      )
    ) {
      setPipelineError('Velg pipeline før du eksporterer deals');
      setStep(2);
      return;
    }

    setPipelineError('');
    setDistributionError('');
    setIsExporting(true);
    setProgress(0);

    const leadsToExport = stagingLeads.filter(lead => selectedLeadIds.includes(lead.id));
    const total = leadsToExport.length;
    let success = 0;
    let companiesCreated = 0;
    let companiesExisting = 0;
    let dealsCreated = 0;
    let dealsExisting = 0;
    let dealsWithoutOwner = 0;
    let dealsSkippedCreateDealOff = 0;
    let skipped = 0;
    let duplicates = 0;
    let errors = 0;
    let existingUsed = 0;
    const skippedDetails = [];
    const existingDetails = [];
    const errorDetails = [];
    const roundRobinUsers = distConfig.selectedOwners.filter(ownerId => validTeamOwnerIds.has(ownerId));

    for (let i = 0; i < total; i += 1) {
      const lead = leadsToExport[i];

      if (lead.wash_status === 'Eksportert') {
        skipped += 1;
        skippedDetails.push(`${lead.name}: allerede eksportert`);
        continue;
      }

      if (isDuplicateLead(lead)) {
        skipped += 1;
        duplicates += 1;
        skippedDetails.push(
          `${lead.name || getCanonicalOrgNumber(lead) || 'Ukjent lead'}: Finnes allerede i CRM`
        );
        continue;
      }

      if (lead.wash_status !== 'Godkjent') {
        skipped += 1;
        skippedDetails.push(`${lead.name}: status er ${lead.wash_status || 'ukjent'}`);
        continue;
      }

      const orgNumber = getCanonicalOrgNumber(lead);

      if (!lead.name && !orgNumber) {
        skipped += 1;
        skippedDetails.push('Ukjent lead: mangler orgnr og navn');
        continue;
      }

      const existingCompany = findExistingCompany(lead);

      if (existingCompany) {
        skipped += 1;
        duplicates += 1;
        skippedDetails.push(
          `${lead.name || orgNumber}: ${existingCompany.reason}`
        );
        continue;
      }

      let ownerId = null;

      if (
        distConfig.mode === 'Auto' &&
        isUuid(lead.suggested_owner_id) &&
        validTeamOwnerIds.has(lead.suggested_owner_id)
      ) {
        ownerId = lead.suggested_owner_id;
      }

      if (
        distConfig.mode === 'Single' &&
        isUuid(distConfig.singleOwnerId) &&
        validTeamOwnerIds.has(distConfig.singleOwnerId)
      ) {
        ownerId = distConfig.singleOwnerId;
      }

      if (distConfig.mode === 'RoundRobin' && roundRobinUsers.length > 0) {
        ownerId = roundRobinUsers[i % roundRobinUsers.length];
      }

      if (
        distConfig.mode === 'Manual' &&
        isUuid(distConfig.manualMap[lead.id]) &&
        validTeamOwnerIds.has(distConfig.manualMap[lead.id])
      ) {
        ownerId = distConfig.manualMap[lead.id];
      }

      try {
        let companyId;
        let companyCreatedForLead = false;
        let dealCompletedForLead = !targetConfig.createDeal;

        const companyData = {
          name: lead.name,
          org_number: orgNumber,
          orgnr: orgNumber,
          orgform: lead.orgform,
          organizationForm: lead.orgform,
          industry: lead.naeringskode_desc,
          naeringskode: lead.naeringskode,
          naeringskode_desc: lead.naeringskode_desc,
          naceCode: lead.naeringskode,
          naceName: lead.naeringskode_desc,
          city: lead.poststed,
          poststed: lead.poststed,
          postnr: lead.postnr,
          kommune: lead.kommune,
          municipalityCode: lead.municipalityCode || lead.kommunenummer || '',
          municipalityName: lead.municipalityName || lead.kommune || '',
          fylke: lead.fylke,
          countyCode: lead.countyCode || lead.fylkesnummer || '',
          countyName: lead.countyName || lead.fylke || '',
          address: lead.address,
          visiting_address: lead.address,
          country: 'NO',
          website: lead.website,
          phone: lead.phone,
          email: lead.email,
          employees: lead.employees,
          employees_count: lead.employees_count,
          status: 'Lead',
          owner_user_id: ownerId,
          source: 'BRREG',
          brregLeadId: lead.id,
          importJobId: lead.job_id,
          gdpr_status: rulesConfig.gdprConfirmed
            ? 'Berettiget interesse'
            : 'Usikker',
          notes: `Importert fra BRREG (Import ID: ${lead.job_id}). ${settings?.license_notice || ''}`,
          postal_code: lead.postnr,
          company_type: lead.orgform,
          nace_code: lead.naeringskode,
          nace_description: lead.naeringskode_desc,
          employees_count: lead.employees_count,
          brreg_status: lead.status_brreg,
          brreg_last_synced_at: new Date().toISOString(),
          brreg_source: 'BRREG',
          brreg_import_job_id: lead.job_id,
          brreg_raw_data: lead.brreg_raw_data
        };

        const companyResult = await addCompany(companyData);

        if (!companyResult?.success) {
          throw new Error(
            companyResult?.error?.message ||
            'Kunne ikke opprette selskap i CRM.'
          );
        }

        companyId = getEntityId(companyResult.data);

        if (!companyId) {
          throw new Error('Nytt selskap mangler en gyldig companyId.');
        }

        companyCreatedForLead = true;
        companiesCreated += 1;

        if (targetConfig.createDeal) {
          const safeStageId =
            targetConfig.stageId &&
            targetConfig.stageId !== 'stage-1' &&
            isUuid(targetConfig.stageId)
              ? targetConfig.stageId
              : validPipelineStages
                ?.map(stage => stage.stage_id || stage.id)
                .find(isUuid) || null;

          const dealTitle = `Salgsmulighet: ${lead.name || orgNumber}`;
          const existingDeal = findExistingDeal(
            companyId,
            selectedPipelineId,
            dealTitle
          );

          if (existingDeal) {
            dealsExisting += 1;
            existingUsed += 1;
            existingDetails.push(
              `${lead.name || orgNumber}: eksisterende deal beholdt`
            );
          } else {
            const dealResult = await addDeal({
              title: dealTitle,
              company_id: companyId,
              companyId,
              companyName: lead.name,
              value: 0,
              status: 'Åpen',
              pipeline_id: selectedPipelineId,
              pipelineId: selectedPipelineId,
              stage_id: safeStageId,
              stageId: safeStageId,
              project_id: targetConfig.targetType === 'Project'
                ? targetConfig.projectId
                : '',
              owner_user_id: ownerId,
              ownerId,
              suggested_owner_id: ownerId,
              source: 'BRREG',
              brregLeadId: lead.id,
              importJobId: lead.job_id
            });

            if (!dealResult?.success) {
              dealCompletedForLead = false;
              throw new Error(
                dealResult?.error?.message ||
                'Kunne ikke opprette deal i CRM.'
              );
            }

            dealsCreated += 1;
            if (!ownerId) dealsWithoutOwner += 1;
          }
        } else {
          dealsSkippedCreateDealOff += 1;
        }

        const statusUpdated = await updateLeadStatus(lead.id, 'Eksportert');

        if (!statusUpdated) {
          throw new Error('CRM-data ble opprettet, men lead-status kunne ikke lagres.');
        }

        success += 1;
      } catch (error) {
        console.error(error);
        errors += 1;
        errorDetails.push(
          `${lead.name || orgNumber || 'Ukjent lead'}: ${error.message || 'Ukjent feil'}`
        );

        const failureStatusUpdated = await updateLeadStatus(lead.id, 'Feil');

        if (!failureStatusUpdated) {
          errorDetails.push(
            `${lead.name || orgNumber || 'Ukjent lead'}: feilstatus kunne ikke lagres`
          );
        }
      }

      setProgress(Math.round(((i + 1) / total) * 100));
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    const selectedPipeline = availablePipelines.find(pipeline => (
      getPipelineId(pipeline) === selectedPipelineId
    ));

    setExportResult({
      success,
      companiesCreated,
      companiesExisting,
      dealsCreated,
      dealsExisting,
      dealsWithoutOwner,
      dealsSkippedCreateDealOff,
      skipped,
      duplicates,
      errors,
      existingUsed,
      pipelineName: selectedPipeline?.name || '',
      pipelineId: selectedPipelineId,
      skippedDetails,
      existingDetails,
      errorDetails
    });
    setIsExporting(false);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white w-full max-w-5xl h-[90vh] rounded-[2rem] shadow-2xl flex flex-col overflow-hidden"
      >
        <div className="px-8 py-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
          <div>
            <h2 className="text-xl font-black text-slate-900">Send til CRM</h2>
            <p className="text-sm font-medium text-slate-600 mt-1">
              Godkjente BRREG-leads kan opprettes som selskaper. Kontakter opprettes ikke automatisk fra BRREG-data.
            </p>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">
              BRREG → CRM
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map(stepNumber => (
                <div
                  key={stepNumber}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-black transition-all ${
                    step === stepNumber
                      ? 'bg-primary-600 text-white shadow-lg'
                      : step > stepNumber
                        ? 'bg-emerald-500 text-white'
                        : 'bg-slate-200 text-slate-400'
                  }`}
                >
                  {step > stepNumber ? <SafeIcon icon="✓" /> : stepNumber}
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={handleClose}
              disabled={isExporting}
              aria-label="Lukk eksportveiviser"
              className="px-3 py-2 rounded-lg text-sm font-black text-slate-500 hover:text-slate-900 hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Lukk
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-8 bg-white relative">
          {step === 1 && (
            <div className="space-y-6">
              <div className="flex gap-6">
                <div className="w-64 space-y-4 p-4 bg-slate-50 rounded-2xl border border-slate-100 h-fit">
                  <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest">Filter</h4>
                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1">
                      Min. Score: {filters.minScore}
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={filters.minScore}
                      onChange={event => setFilters({
                        ...filters,
                        minScore: Number(event.target.value)
                      })}
                      className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-sm font-bold text-slate-700">
                    <input
                      type="checkbox"
                      checked={filters.hasEmail}
                      onChange={event => setFilters({
                        ...filters,
                        hasEmail: event.target.checked
                      })}
                      className="rounded text-primary-600"
                    />
                    Kun med e-post
                  </label>
                  <label className="flex items-center gap-2 text-sm font-bold text-slate-700">
                    <input
                      type="checkbox"
                      checked={filters.hasPhone}
                      onChange={event => setFilters({
                        ...filters,
                        hasPhone: event.target.checked
                      })}
                      className="rounded text-primary-600"
                    />
                    Kun med telefon
                  </label>
                  <button
                    onClick={() => handleSelectAll(true)}
                    className="w-full py-2 bg-white border border-slate-200 rounded-lg text-xs font-bold text-primary-600 hover:bg-primary-50"
                  >
                    Velg alle filtrerte
                  </button>
                </div>

                <div className="flex-1 overflow-hidden border border-slate-200 rounded-2xl">
                  <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 flex justify-between items-center">
                    <span className="text-xs font-black text-slate-500 uppercase tracking-widest">
                      {selectedLeadIds.length} leads valgt
                    </span>
                    <span className="text-xs font-bold text-slate-400">
                      Viser {filteredLeads.length} godkjente
                    </span>
                  </div>
                  {filteredLeads.length === 0 && (
                    <div className="mx-4 mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm font-medium text-amber-800">
                      Ingen godkjente leads tilgjengelig for eksport. Gå tilbake til Vask & Godkjenn og godkjenn minst én lead først.
                    </div>
                  )}
                  <div className="overflow-y-auto max-h-[500px]">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-white sticky top-0 z-10 shadow-sm">
                        <tr>
                          <th className="px-4 py-3 w-10">
                            <input
                              type="checkbox"
                              onChange={event => handleSelectAll(event.target.checked)}
                            />
                          </th>
                          <th className="px-4 py-3 font-bold text-slate-700">Navn</th>
                          <th className="px-4 py-3 font-bold text-slate-700">Score</th>
                          <th className="px-4 py-3 font-bold text-slate-700">Sted</th>
                          <th className="px-4 py-3 font-bold text-slate-700">Kontakt</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {filteredLeads.map(lead => (
                          <tr
                            key={lead.id}
                            className={`hover:bg-slate-50 ${
                              selectedLeadIds.includes(lead.id)
                                ? 'bg-blue-50/30'
                                : ''
                            }`}
                          >
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={selectedLeadIds.includes(lead.id)}
                                onChange={() => handleToggleLead(lead.id)}
                              />
                            </td>
                            <td className="px-4 py-3 font-medium">
                              {lead.name}
                              <span className="text-xs text-slate-400 block">
                                {getCanonicalOrgNumber(lead)}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                lead.data_quality_score > 50
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-yellow-100 text-yellow-700'
                              }`}>
                                {lead.data_quality_score}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-slate-500">{lead.poststed}</td>
                            <td className="px-4 py-3 flex gap-2">
                              {lead.email && (
                                <span title="E-post" className="text-blue-500">📧</span>
                              )}
                              {lead.phone && (
                                <span title="Telefon" className="text-green-500">📞</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="max-w-2xl mx-auto space-y-8">
              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
                <h3 className="font-black text-slate-900 text-lg">Mål for data</h3>
                <div className="flex gap-4">
                  <button
                    onClick={() => setTargetConfig({
                      ...targetConfig,
                      targetType: 'Global'
                    })}
                    className={`flex-1 p-4 rounded-xl border-2 text-center transition-all ${
                      targetConfig.targetType === 'Global'
                        ? 'border-primary-600 bg-primary-50 text-primary-700'
                        : 'border-slate-100 hover:border-slate-200'
                    }`}
                  >
                    <SafeIcon icon="🌐" className="block mx-auto text-2xl mb-2" />
                    <span className="font-bold">Global CRM</span>
                  </button>
                  <button
                    onClick={() => setTargetConfig({
                      ...targetConfig,
                      targetType: 'Project'
                    })}
                    className={`flex-1 p-4 rounded-xl border-2 text-center transition-all ${
                      targetConfig.targetType === 'Project'
                        ? 'border-primary-600 bg-primary-50 text-primary-700'
                        : 'border-slate-100 hover:border-slate-200'
                    }`}
                  >
                    <SafeIcon icon="🏗️" className="block mx-auto text-2xl mb-2" />
                    <span className="font-bold">Prosjekt CRM</span>
                  </button>
                </div>

                {targetConfig.targetType === 'Project' && (
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                      Velg Prosjekt
                    </label>
                    <select
                      className="w-full p-3 border border-slate-200 rounded-xl"
                      value={targetConfig.projectId}
                      onChange={event => setTargetConfig({
                        ...targetConfig,
                        projectId: event.target.value
                      })}
                    >
                      <option value="">-- Velg --</option>
                      {projects.map(project => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {targetConfig.targetType === 'Project' && !targetConfig.createDeal && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                    <p className="text-sm font-bold leading-6 text-amber-900">
                      Prosjekt CRM er valgt, men deal-opprettelse er av. Selskapene blir opprettet under Selskaper, men vises ikke i Pipeline før det opprettes en deal.
                    </p>
                  </div>
                )}
              </div>

              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-black text-slate-900 text-lg">Automatisering</h3>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={targetConfig.createDeal}
                      onChange={event => setTargetConfig({
                        ...targetConfig,
                        createDeal: event.target.checked
                      })}
                    />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600" />
                  </label>
                </div>
                <p className="text-sm font-bold text-slate-700">
                  Opprett salgsmulighet i valgt pipeline
                </p>
                <p className="text-sm text-slate-500">
                  Må være aktivert hvis du vil at BRREG-leads skal vises i Pipeline.
                </p>

                {targetConfig.createDeal && (
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                        Velg pipeline for dealene
                      </label>
                      <select
                        className="w-full p-3 border border-slate-200 rounded-xl bg-slate-50"
                        value={targetConfig.pipelineId}
                        onChange={event => setTargetConfig({
                          ...targetConfig,
                          pipelineId: event.target.value
                        })}
                      >
                        <option value="">-- Velg pipeline --</option>
                        {availablePipelines.map(pipeline => {
                          const pipelineId = getPipelineId(pipeline);

                          return (
                            <option key={pipelineId} value={pipelineId}>
                              {pipeline.name}
                            </option>
                          );
                        })}
                      </select>
                      {targetConfig.pipelineId ? (
                        <p className="mt-2 text-sm font-bold text-blue-700">
                          Deals opprettes i:{' '}
                          {availablePipelines.find(pipeline => (
                            getPipelineId(pipeline) === targetConfig.pipelineId
                          ))?.name || targetConfig.pipelineId}
                        </p>
                      ) : (
                        <p className="mt-2 text-sm font-bold text-amber-700">
                          Velg pipeline for dealene før du fortsetter.
                        </p>
                      )}
                      {pipelineError && (
                        <p className="mt-2 text-sm font-bold text-rose-600">
                          {pipelineError}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                        Fase (Stage)
                      </label>
                      <select
                        className="w-full p-3 border border-slate-200 rounded-xl"
                        value={targetConfig.stageId}
                        onChange={event => setTargetConfig({
                          ...targetConfig,
                          stageId: event.target.value
                        })}
                      >
                        {validPipelineStages.map(stage => (
                          <option key={stage.stage_id} value={stage.stage_id}>
                            {stage.stage_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="max-w-2xl mx-auto space-y-8">
              <div className="space-y-4">
                <h3 className="font-black text-slate-900 text-lg">Eierskap & Fordeling</h3>
                <div className="space-y-2">
                  {[
                    { id: 'Auto', label: 'Behold foreslått eierskap (fra import)', icon: '🤖' },
                    { id: 'RoundRobin', label: 'Round-Robin (Rullering)', icon: '🔄' },
                    { id: 'Single', label: 'Tildel alle til én selger', icon: '👤' }
                  ].map(mode => (
                    <button
                      key={mode.id}
                      onClick={() => {
                        setDistributionError('');
                        setDistConfig({
                          ...distConfig,
                          mode: mode.id
                        });
                      }}
                      className={`w-full flex items-center p-4 rounded-xl border-2 transition-all text-left ${
                        distConfig.mode === mode.id
                          ? 'border-primary-600 bg-primary-50'
                          : 'border-slate-100 hover:border-slate-200 bg-white'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 ${
                        distConfig.mode === mode.id
                          ? 'bg-primary-600 text-white'
                          : 'bg-slate-100 text-slate-500'
                      }`}>
                        <SafeIcon icon={mode.icon} />
                      </div>
                      <span className={`font-bold ${
                        distConfig.mode === mode.id
                          ? 'text-primary-900'
                          : 'text-slate-700'
                      }`}>
                        {mode.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {distConfig.mode === 'RoundRobin' && (
                <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200">
                  <h4 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-3">
                    Velg selgere i rotasjonen
                  </h4>
                  {validTeamUsers.length === 0 ? (
                    <p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm font-bold text-amber-800">
                      Ingen team-brukere tilgjengelig. Eksporteres uten eier.
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {validTeamUsers.map(user => {
                        const ownerId = user.ownerId;
                        const isSelected = distConfig.selectedOwners.includes(ownerId);

                        return (
                          <button
                            key={ownerId}
                            onClick={() => {
                              const newSelection = isSelected
                                ? distConfig.selectedOwners.filter(id => id !== ownerId)
                                : [...distConfig.selectedOwners, ownerId];

                              setDistConfig({
                                ...distConfig,
                                selectedOwners: newSelection
                              });
                            }}
                            className={`px-3 py-1.5 rounded-lg text-sm font-bold border transition-all ${
                              isSelected
                                ? 'bg-primary-600 text-white border-primary-600'
                                : 'bg-white text-slate-600 border-slate-200'
                            }`}
                          >
                            {user.name || user.email || 'Ukjent bruker'}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {distConfig.selectedOwners.length === 0 && (
                    <p className="mt-3 rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm font-bold text-blue-800">
                      Ingen selgere er valgt. Leads eksporteres uten eier og kan plukkes fra pipeline senere.
                    </p>
                  )}
                </div>
              )}

              {distConfig.mode === 'Single' && (
                <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200">
                  <h4 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-3">
                    Velg ansvarlig
                  </h4>
                  {validTeamUsers.length === 0 ? (
                    <p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm font-bold text-amber-800">
                      Ingen team-brukere tilgjengelig. Velg eksport uten eier.
                    </p>
                  ) : (
                    <select
                      className="w-full p-3 border border-slate-200 rounded-xl bg-white"
                      value={distConfig.singleOwnerId}
                      onChange={event => {
                        setDistributionError('');
                        setDistConfig({
                          ...distConfig,
                          singleOwnerId: event.target.value
                        });
                      }}
                    >
                      <option value="">-- Velg bruker --</option>
                      {validTeamUsers.map(user => (
                        <option key={user.ownerId} value={user.ownerId}>
                          {user.name || user.email || 'Ukjent bruker'}
                        </option>
                      ))}
                    </select>
                  )}
                  {distributionError && (
                    <p className="mt-3 text-sm font-bold text-rose-600">
                      {distributionError}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="max-w-2xl mx-auto space-y-8">
              <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm space-y-6">
                <h3 className="font-black text-slate-900 text-lg">Personvern & Regler</h3>
                <label className="flex items-start gap-4 p-4 bg-primary-50 rounded-xl border border-primary-100 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rulesConfig.gdprConfirmed}
                    onChange={event => setRulesConfig({
                      ...rulesConfig,
                      gdprConfirmed: event.target.checked
                    })}
                    className="mt-1 w-5 h-5 text-primary-600 rounded"
                  />
                  <div>
                    <span className="block font-bold text-primary-900 text-sm">
                      Merk med "Berettiget interesse" (GDPR)
                    </span>
                    <span className="block text-xs text-primary-700 mt-1">
                      Jeg bekrefter at vi har juridisk grunnlag for å lagre disse dataene.
                    </span>
                  </div>
                </label>
                <div className="space-y-4 pt-4 border-t border-slate-100">
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked
                      readOnly
                      className="w-5 h-5 rounded text-slate-900"
                    />
                    <span className="text-sm font-medium text-slate-700">
                      Kontakter opprettes ikke automatisk fra BRREG-data
                    </span>
                  </label>
                </div>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl text-xs text-slate-500 border border-slate-100 flex gap-3">
                <SafeIcon icon="ℹ️" className="w-5 h-5 flex-shrink-0" />
                <div>
                  <p className="font-bold mb-1">Datakilde & Lisens</p>
                  <p>
                    Inneholder data under Norsk lisens for offentlige data (NLOD) tilgjengeliggjort av Brønnøysundregistrene.
                  </p>
                </div>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="max-w-2xl mx-auto space-y-8 text-center">
              {!isExporting && !exportResult ? (
                <>
                  <div className="w-24 h-24 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <SafeIcon icon="🚀" className="w-12 h-12 text-primary-600" />
                  </div>
                  <h2 className="text-3xl font-black text-slate-900">Klar til eksport</h2>
                  <p className="text-slate-500 font-medium text-lg">
                    Du er i ferd med å overføre selskapsdata til CRM.
                  </p>
                  <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200 text-left space-y-3 max-w-sm mx-auto mt-8">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Antall selskaper:</span>
                      <span className="font-bold">{selectedLeadIds.length}</span>
                    </div>
                    {targetConfig.createDeal && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Antall deals:</span>
                        <span className="font-bold">{selectedLeadIds.length}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Mål:</span>
                      <span className="font-bold">{targetConfig.targetType}</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="space-y-8 py-12">
                  {!exportResult ? (
                    <>
                      <h3 className="text-xl font-bold text-slate-900 animate-pulse">
                        Eksporterer data...
                      </h3>
                      <div className="w-full bg-slate-100 rounded-full h-4 overflow-hidden max-w-md mx-auto">
                        <motion.div
                          className="h-full bg-primary-600"
                          initial={{ width: 0 }}
                          animate={{ width: `${progress}%` }}
                        />
                      </div>
                      <p className="text-sm font-bold text-primary-600">
                        {progress}% fullført
                      </p>
                    </>
                  ) : (
                    <motion.div
                      initial={{ scale: 0.9, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                    >
                      <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 ${
                        exportResult.errors > 0
                          ? 'bg-amber-100'
                          : 'bg-emerald-100'
                      }`}>
                        <SafeIcon
                          icon={exportResult.errors > 0 ? '⚠️' : '✅'}
                          className={`w-12 h-12 ${
                            exportResult.errors > 0
                              ? 'text-amber-600'
                              : 'text-emerald-600'
                          }`}
                        />
                      </div>
                      <h2 className="text-3xl font-black text-slate-900 mb-2">
                        {exportResult.errors > 0
                          ? exportResult.success > 0
                            ? 'Eksport fullført med avvik'
                            : 'Eksport feilet'
                          : 'Eksport fullført'}
                      </h2>
                      <div className="flex justify-center gap-8 mt-6 text-sm">
                        {exportResult.companiesCreated > 0 && (
                          <div className="text-center">
                            <p className="font-black text-2xl text-emerald-600">
                              {exportResult.companiesCreated}
                            </p>
                            <p className="text-slate-500 uppercase tracking-widest text-[10px]">
                              Selskaper opprettet
                            </p>
                          </div>
                        )}
                        {exportResult.dealsCreated > 0 && (
                          <div className="text-center">
                            <p className="font-black text-2xl text-blue-600">
                              {exportResult.dealsCreated}
                            </p>
                            <p className="text-slate-500 uppercase tracking-widest text-[10px]">
                              Deals opprettet
                            </p>
                          </div>
                        )}
                        {exportResult.skipped > 0 && (
                          <div className="text-center">
                            <p className="font-black text-2xl text-slate-400">
                              {exportResult.skipped}
                            </p>
                            <p className="text-slate-500 uppercase tracking-widest text-[10px]">
                              Leads hoppet over
                            </p>
                          </div>
                        )}
                        {exportResult.duplicates > 0 && (
                          <div className="text-center">
                            <p className="font-black text-2xl text-orange-600">
                              {exportResult.duplicates}
                            </p>
                            <p className="text-slate-500 uppercase tracking-widest text-[10px]">
                              Duplikater
                            </p>
                          </div>
                        )}
                        {exportResult.errors > 0 && (
                          <div className="text-center">
                            <p className="font-black text-2xl text-rose-600">
                              {exportResult.errors}
                            </p>
                            <p className="text-slate-500 uppercase tracking-widest text-[10px]">
                              Feilet
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 text-left">
                        {exportResult.companiesExisting > 0 && (
                          <p className="rounded-lg bg-blue-50 p-3 text-xs font-bold text-blue-700">
                            Eksisterende selskaper funnet: {exportResult.companiesExisting}
                          </p>
                        )}
                        {exportResult.dealsExisting > 0 && (
                          <p className="rounded-lg bg-violet-50 p-3 text-xs font-bold text-violet-700">
                            Eksisterende deals beholdt: {exportResult.dealsExisting}
                          </p>
                        )}
                      </div>
                      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-left">
                        {exportResult.dealsSkippedCreateDealOff > 0 && (
                          <p className="rounded-lg bg-amber-50 p-3 text-xs font-bold text-amber-700">
                            Deals hoppet over fordi createDeal var av: {exportResult.dealsSkippedCreateDealOff}
                          </p>
                        )}
                      </div>
                      {!targetConfig.createDeal && (
                        <div className="mt-8 text-left bg-amber-50 rounded-xl p-4 border border-amber-200">
                          <p className="text-sm font-bold text-amber-900">
                            Ingen deals ble opprettet.
                          </p>
                          {targetConfig.targetType === 'Project' && (
                            <p className="mt-2 text-sm text-amber-800">
                              Prosjekt CRM er valgt, men deal-opprettelse er av. Selskapene er opprettet i Selskaper, men vises ikke i pipeline før det opprettes en deal.
                            </p>
                          )}
                        </div>
                      )}
                      {targetConfig.createDeal && exportResult.dealsCreated > 0 && (
                        <div className="mt-8 text-left bg-blue-50 rounded-xl p-4 border border-blue-200">
                          <p className="text-sm font-bold text-blue-900">
                            Deals eksportert til pipeline:{' '}
                            {exportResult.pipelineName || exportResult.pipelineId}
                          </p>
                        </div>
                      )}
                      {targetConfig.createDeal && exportResult.dealsWithoutOwner > 0 && (
                        <div className="mt-4 text-left bg-amber-50 rounded-xl p-4 border border-amber-200">
                          <p className="text-sm font-bold text-amber-900">
                            Noen deals ble eksportert uten eier og kan plukkes fra pipeline senere.
                          </p>
                        </div>
                      )}
                      {exportResult.existingDetails?.length > 0 && (
                        <div className="mt-8 text-left bg-blue-50 rounded-xl p-4">
                          <p className="text-xs font-black uppercase tracking-widest text-blue-600 mb-2">
                            CRM-duplikater
                          </p>
                          <ul className="space-y-1 text-xs text-blue-700">
                            {exportResult.existingDetails.map((item, index) => (
                              <li key={index}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {exportResult.skippedDetails?.length > 0 && (
                        <div className="mt-8 text-left bg-slate-50 rounded-xl p-4">
                          <p className="text-xs font-black uppercase tracking-widest text-slate-500 mb-2">
                            Leads hoppet over
                          </p>
                          <ul className="space-y-1 text-xs text-slate-600">
                            {exportResult.skippedDetails.map((item, index) => (
                              <li key={index}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {exportResult.errorDetails?.length > 0 && (
                        <div className="mt-4 text-left bg-rose-50 rounded-xl p-4">
                          <p className="text-xs font-black uppercase tracking-widest text-rose-600 mb-2">
                            Feilet
                          </p>
                          <ul className="space-y-1 text-xs text-rose-700">
                            {exportResult.errorDetails.map((item, index) => (
                              <li key={index}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </motion.div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-8 py-5 border-t border-slate-100 bg-slate-50/50 flex justify-between items-center">
          <div className="flex items-center gap-3">
            {step > 1 && !exportResult ? (
              <button
                onClick={() => setStep(current => current - 1)}
                disabled={isExporting}
                className="px-6 py-3 text-slate-500 font-bold text-sm hover:text-slate-800 disabled:opacity-50"
              >
                Tilbake
              </button>
            ) : null}
            {!exportResult && (
              <button
                type="button"
                onClick={handleClose}
                disabled={isExporting}
                className="px-6 py-3 border border-slate-200 bg-white text-slate-600 font-bold text-sm rounded-xl hover:bg-slate-100 hover:text-slate-900 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Avbryt
              </button>
            )}
          </div>

          {!exportResult ? (
            <button
              onClick={handleNextStep}
              disabled={
                (step === 1 && selectedLeadIds.length === 0) ||
                (step === 4 && !rulesConfig.gdprConfirmed) ||
                isExporting
              }
              className="px-8 py-3 bg-primary-600 text-white font-black text-sm rounded-xl hover:bg-primary-700 shadow-lg shadow-primary-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {step === 5
                ? isExporting
                  ? 'Jobber...'
                  : 'Start Eksport 🚀'
                : 'Neste Steg'}
              {step < 5 && <SafeIcon icon="➡️" className="w-4 h-4" />}
            </button>
          ) : (
            <button
              onClick={handleClose}
              className="px-8 py-3 bg-slate-900 text-white font-black text-sm rounded-xl hover:bg-slate-800 shadow-xl"
            >
              Lukk og gå til CRM
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ExportWizard;